import { Box } from '../joy/Box';
import { Card } from '../joy/Card';
import { Table } from '../joy/Table';
import { IconButton } from '../joy/IconButton';
import { Tooltip } from '../joy/Tooltip';
import { Grid } from '../joy/Grid';
import { ButtonGroup } from '../joy/ButtonGroup';
import { TablePagination } from '../custom/TablePagination';
import { Trans } from '@lingui/react';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useState } from 'react';
import React from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Stack } from '../joy/Stack';
import { Link } from '../joy/Link';
import { visuallyHidden } from '@mui/utils';
import { Skeleton } from '../joy/Skeleton';
import { CircularProgress } from '../joy/CircularProgress';
import { Button } from '../joy/Button';

import { Row } from './Row';
import { DraggableCard } from './DraggableCard';
import { BaseItem, ItemListProps } from './types';

export { type BaseItem, type ItemListProps, type Action, type Column } from './types';

export const ItemList = <T extends BaseItem>({
  items,
  onItemClick,
  linkTo,
  columns: columnsProp = [],
  renderCard,
  onDelete,
  defaultView = 'table',
  onDrop,
  actions,
  globalActions,
  globalActionsChildren,
  pagination,
  sort,
  setSort,
  emptyState,
  extraRow,
  skeletonCount,
  beforeTableSection,
  pending: pendingProp,
}: ItemListProps<T>) => {
  const [pending, startTransition] = React.useTransition();
  const [isTableView, setIsTableView] = useState(defaultView === 'table');

  const columns = columnsProp.filter(column => column.show ?? true);

  const showActions = Boolean(actions) || items.some(item => item.deletable);
  const columnCount = columns.length + (extraRow?.toggle ? 1 : 0) + (showActions ? 1 : 0);

  return (
    <DndProvider backend={HTML5Backend}>
      <Stack justifyContent="space-between" gap={1} mb={1}>
        {renderCard && (
          <ButtonGroup>
            <Tooltip title={<Trans id="Grid view" />}>
              <IconButton
                onClick={() => setIsTableView(false)}
                color={isTableView ? 'neutral' : 'primary'}
                variant={isTableView ? 'plain' : 'solid'}
              >
                <GridViewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={<Trans id="List view" />}>
              <IconButton
                onClick={() => setIsTableView(true)}
                color={isTableView ? 'primary' : 'neutral'}
                variant={isTableView ? 'solid' : 'plain'}
              >
                <ViewListIcon />
              </IconButton>
            </Tooltip>
          </ButtonGroup>
        )}
      </Stack>
      {isTableView ? (
        <Card sx={{ position: 'relative' }}>
          {(pending || pendingProp) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
              }}
            >
              <CircularProgress size="sm" />
            </Box>
          )}
          <Stack direction="row" justifyContent="space-between">
            {beforeTableSection}

            {(globalActions || globalActionsChildren) && (
              <Stack gap={1} direction="row" justifyContent="flex-end">
                {globalActions?.map((action, index) =>
                  action.icon ? (
                    <Tooltip key={index} title={action.label}>
                      <IconButton onClick={action.onClick}>{action.icon}</IconButton>
                    </Tooltip>
                  ) : (
                    <Button key={index} onClick={action.onClick} {...action.buttonProps}>
                      {action.label}
                    </Button>
                  ),
                )}
                {globalActionsChildren}
              </Stack>
            )}
          </Stack>
          <Table noWrap hoverRow={!emptyState} sx={{ '& tr': { cursor: 'pointer' } }}>
            <thead>
              <tr>
                {extraRow?.toggle && <th />}
                {columns.map((column, index) => {
                  const active = sort?.field === column.sortKey;
                  return (
                    <th key={index} style={{ width: column.width }} align={column.align}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent={column.align === 'right' ? 'flex-end' : 'flex-start'}
                      >
                        <Link
                          underline="none"
                          color="neutral"
                          textColor={active ? 'primary.plainColor' : undefined}
                          component="button"
                          onClick={() => {
                            startTransition(() => {
                              if (column.sortKey && sort) {
                                setSort?.({
                                  field: column.sortKey,
                                  direction:
                                    sort.field !== column.sortKey
                                      ? sort.direction
                                      : sort.direction === 'DESC'
                                        ? 'ASC'
                                        : 'DESC',
                                });
                              }
                            });
                          }}
                          endDecorator={
                            column.sortKey ? (
                              <ArrowDownwardIcon sx={[active ? { opacity: 1 } : { opacity: 0 }]} />
                            ) : null
                          }
                          sx={{
                            color: column.sortKey ? 'primary.plainColor' : 'neutral',
                            cursor: column.sortKey ? 'pointer' : 'default',
                            fontWeight: 'lg',

                            '& svg': {
                              transition: '0.2s',
                              transform: active && sort?.direction === 'DESC' ? 'rotate(0deg)' : 'rotate(180deg)',
                            },

                            '&:hover': { '& svg': { opacity: 1 } },
                          }}
                        >
                          {column.header}
                          {active ? (
                            <Box component="span" sx={visuallyHidden}>
                              {sort?.direction === 'DESC' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                          ) : null}
                        </Link>
                      </Stack>
                    </th>
                  );
                })}
                {showActions && <th style={{ textAlign: 'right' }} />}
              </tr>
            </thead>
            <tbody>
              {emptyState ? (
                <tr>
                  <td colSpan={columnCount}>{emptyState}</td>
                </tr>
              ) : skeletonCount ? (
                Array.from({ length: skeletonCount }).map((_, index) => (
                  <tr key={index}>
                    {columns.map((column, colIndex) => (
                      <td key={colIndex}>
                        <Skeleton variant="text" width={column.width || '100%'} />
                      </td>
                    ))}
                    <td style={{ textAlign: 'right' }}>
                      <ButtonGroup sx={{ justifyContent: 'flex-end', gap: 1 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </ButtonGroup>
                    </td>
                  </tr>
                ))
              ) : (
                items.map(item => (
                  <Row
                    key={item.id}
                    item={item}
                    columns={columns}
                    onDelete={onDelete}
                    onItemClick={onItemClick}
                    linkTo={linkTo}
                    onDrop={onDrop}
                    actions={actions}
                    extraRow={extraRow}
                    columnCount={columnCount}
                  />
                ))
              )}
            </tbody>
            {pagination && (
              <tfoot>
                <tr>
                  <td colSpan={columnCount}>
                    <TablePagination
                      totalRows={pagination.totalCount}
                      page={pagination.page}
                      setPage={(page: number) => {
                        startTransition(() => {
                          pagination.setPage(page);
                        });
                      }}
                      rowsPerPage={pagination.rowsPerPage}
                      setRowsPerPage={(rows: number) => {
                        startTransition(() => {
                          pagination.setRowsPerPage(rows);
                          pagination.setPage(0);
                        });
                      }}
                    />
                  </td>
                </tr>
              </tfoot>
            )}
          </Table>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {items.map(item => (
            <DraggableCard key={item.id} item={item} onItemClick={onItemClick} onDrop={onDrop}>
              {renderCard?.(item)}
            </DraggableCard>
          ))}
        </Grid>
      )}
    </DndProvider>
  );
};
